import React from "react";
import "./TimelineDisplay.css";

/**
 * IMPORTANT:
 * Keep your structure/comments here. This file now exports buildTimelineData()
 * so other pages (Timeline tracker) can render the SAME generated events.
 */
const events = {
  campaigns: {
    "People of the Dream Keeper": [{ year: 1, text: "📖 Dreamless Respite" }],
  },

  pillars: {
    "Arc Survivors": { year: 1, text: "📖 First Meal" },
    "Encounters": { year: 3, text: "📖 Missing Statue" },
    "Scouts": { year: 1, text: "📖 Extinguished Guidepost" },
  },

  monsters: {
    //Noe 1 Quarry
    "Gorm": { year: 1, text: "📖 The Approaching Storm" },
    "Crimson Crocodile": { year: 0, text: "📖 Devour the White Lion" },
    "White Lion": { year: 0, text: "📖 The First Story" },

    //Node 2 Quarry
    "Screaming Antelope": { year: 2, text: "📖 Endless Screams" },
    "Spidicules": { year: 2, text: "📖 Young Rivals" },
    "Flower Knight": { year: 5, text: "📖 A Crone's Tale" },
    "Smog Singers": { year: 2, text: "📖 Death Song" },

    //Node 3 Quarry
    "Phoenix": { year: 7, text: "📖 Phoenix Feather" },
    "Dragon King": { year: 8, text: "📖 Glowing Crater" },
    "Sunstalker": { year: 8, text: "📖 Promise Under the Sun" },

    //Node 4 Quarry
    "Dung Beetle Knight": { year: 8, text: "📖 Rumbling in the Dark" },
    "Lion God": { year: 13, text: "📖 The Silver City" },
    "The King": { year: 22, text: "📖 The Awaited" },

    //Node 1 Nemesis
    "Butcher": [
      { year: 4, text: "⚔️ Nemesis Encounter - Butcher Lvl 1" },
      { year: 16, text: "⚔️ Nemesis Encounter - Butcher Lvl 2" },
      { year: 23, text: "⚔️ Nemesis Encounter - Butcher Lvl 3" },
    ],
    "Manhunter": [{ year: 5, text: "📖 The Hanged Man" }],

    //Node 2 Nemesis
    "King's Man": [
      { year: 6, text: "📖 Armored Strangers" },
      { year: 9, text: "⚔️ Nemesis Encounter - King's Man Lvl 1" },
      { year: 19, text: "⚔️ Nemesis Encounter - King's Man Lvl 2" },
      { year: 28, text: "⚔️ Nemesis Encounter- King's Man Lvl 3" },
    ],
    "Lion Knight": { year: 6, text: "📖 An Uninvited Guest" },
    "Slenderman": { year: 6, text: "📖 It's Already Here" },
    "Atnas": [
      { year: 6, text: "📖 Unwanted Gifts" },
      { year: 9, text: "⚔️ Nemesis Encounter - Atnas Lvl 1" },
      { year: 18, text: "⚔️ Nemesis Encounter - Atnas Lvl 2" },
      { year: 28, text: "⚔️ Nemesis Encounter- Atnas Lvl 3" },
    ],

    //Node 3 Nemesis
    "The Hand": [{ year: 11, text: "📖 Regal Visit" }],

    //Core Monster
    "Watcher": [
      { year: 20, text: "📖 Watched" },
      { year: 25, text: "⚔️ Nemesis Encounter - Watcher" },
    ],
    "The Gambler": [
      { year: 5, text: "📖 Stained" },
      { year: 11, text: "📖 The Game" },
      { year: 15, text: "📖 Wonderous Design" },
      { year: 20, text: "📖 Perfect Punt" },
      { year: 20, text: "⚔️ Nemesis Encounter - The Gambler" },
      { year: 21, text: "📖 Lantern Festival" },
    ],

    // Commented out Until I fix there naming
    //"Dragon King": [{ year: 8, text: "📖 Glowing Crater" }],
    //"Sunstalker": [{ year: 8, text: "📖 Promise Under the Sun" }],

    //Finale Monsters
    "Gold Smoke Knight": [{ year: 30, text: "⚔️ Nemesis Encounter - Gold Smoke Knight" }],
    "Godhand": [{ year: 30, text: "⚔️ Nemesis Encounter - Godhand" }],
  },
};

function toEventArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "object") return [value];
  return [];
}

function normalizeMonsters(selectedMonsters) {
  // Accept array OR object
  if (Array.isArray(selectedMonsters)) return selectedMonsters.filter(Boolean);
  if (selectedMonsters && typeof selectedMonsters === "object") {
    return Object.values(selectedMonsters).filter(Boolean);
  }
  return [];
}

function inferPrologueMonster(selectedMonsters, monsterList) {
  // If object node structure exists, keep that behavior
  if (selectedMonsters && typeof selectedMonsters === "object" && !Array.isArray(selectedMonsters)) {
    return selectedMonsters["Node 1 Quarry"] === "Crimson Crocodile" ? "Crimson Crocodile" : "White Lion";
  }
  return monsterList.includes("Crimson Crocodile") ? "Crimson Crocodile" : "White Lion";
}

function addEventToBuckets({ timeline, prologueEvents }, evtText, year, { prepend = false } = {}) {
  const y = Number(year);
  if (y === 0) {
    prologueEvents.push(evtText);
    return;
  }
  if (y >= 1 && y <= timeline.length) {
    if (prepend) timeline[y - 1].events.unshift(evtText);
    else timeline[y - 1].events.push(evtText);
  }
}

/**
 * Exported: canonical timeline generator.
 * Returns:
 * {
 *   prologueMonster,
 *   prologueEvents: [string],
 *   years: [{ year: number, events: [string] }, ...]
 * }
 */
export function buildTimelineData({
  selectedCampaign = "",
  selectedPillars = [],
  selectedMonsters = [],
  yearsCount = 39,
} = {}) {
  const timeline = Array.from({ length: yearsCount }, (_, i) => ({ year: i + 1, events: [] }));
  const prologueEvents = [];

  const monsterList = normalizeMonsters(selectedMonsters);
  const prologueMonster = inferPrologueMonster(selectedMonsters, monsterList);

  // 1) Campaign events first (so DK year1 can be above others)
  const campList = toEventArray(events.campaigns[selectedCampaign]);
  campList.forEach((evt) => {
    const prepend =
      selectedCampaign === "People of the Dream Keeper" && Number(evt.year) === 1;
    addEventToBuckets({ timeline, prologueEvents }, evt.text, evt.year, { prepend });
  });

  // 2) Pillars
  (selectedPillars || []).forEach((pillar) => {
    const list = toEventArray(events.pillars[pillar]);
    list.forEach((evt) => addEventToBuckets({ timeline, prologueEvents }, evt.text, evt.year));
  });

  // 3) Monsters
  monsterList.forEach((monster) => {
    const list = toEventArray(events.monsters[monster]);
    list.forEach((evt) => addEventToBuckets({ timeline, prologueEvents }, evt.text, evt.year));
  });

  return { prologueMonster, prologueEvents, years: timeline };
}

const TimelineDisplay = ({ selectedCampaign = "", selectedPillars = [], selectedMonsters = [] }) => {
  const { prologueMonster, prologueEvents, years } = buildTimelineData({
    selectedCampaign,
    selectedPillars,
    selectedMonsters,
  });

  return (
    <div className="timeline">
      <div className="timeline-prologue">
        <h3>Prologue: {prologueMonster}</h3>
        {prologueEvents.length > 0 ? (
          <ul>
            {prologueEvents.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        ) : null}
      </div>

      {years.map(({ year, events }) => (
        <div key={year} className="timeline-year">
          <h3>Year {year}</h3>
          {events.length > 0 ? (
            <ul>
              {events.map((event, index) => (
                <li key={index}>{event}</li>
              ))}
            </ul>
          ) : (
            <p>No events</p>
          )}
        </div>
      ))}
    </div>
  );
};

export default TimelineDisplay;